import { hash } from 'spark-md5';

import { extractedSegmentsToDiffSegments } from './extractedSegmentsToDiffSegments';
import type { HtmlEditApplierDeps, HtmlEditApplierMode, ParagraphInfo } from './shared';
import {
  createRedlineEditError,
  documentIdSchema,
  getIsCancellationError,
  stripSelectionMarkers,
} from './shared';
import { getIsPdfLocationAnchor } from '../../extract-paragraphs/pdf/locationAnchor';
import {
  AlineTransforms,
  applyInParagraphEditOutput,
  createVisualDiffInRange,
  generateSuggestedEditGroupId,
} from '../../slate-api';
import type { EditParagraphSuggestion } from '../redline-llm-output';
import {
  extractSingleRangeContent,
  findFirstParagraphElement,
  hastToReviewRanges,
  parseHtmlToHast,
} from '../redline-llm-output';
import type { CodeBlockRedlineEdit, CodeBlockRedlineItem } from '../redline-shapes';

/**
 * Apply one streamed in-paragraph `edit` suggestion to the server-side editor
 * and convert it into the `redline-edit` / `redline-edit-error` rows the client
 * will render. The proposed HTML is parsed into ranges keyed against the
 * existing paragraph, each failure mode (invalid documentId, missing `<p>`,
 * missing paragraph, no-op formatting-only edit, throw from apply) becomes a
 * typed `redline-edit-error` so the stream stays well-formed.
 */
export async function applyInParagraphSuggestion(
  suggest: EditParagraphSuggestion,
  deps: HtmlEditApplierDeps,
  mode: HtmlEditApplierMode
): Promise<CodeBlockRedlineItem[]> {
  const { editorPool, errorReporter, logger, makeSource, paragraphState, userId } = deps;
  const { documentId, html: rawHtml, id: paragraphId } = suggest;

  /**
   * The editor LLM occasionally emits a non-document `documentId` — most often
   * the literal "inline". The edit schema types it as a loose `z.string()`,
   * but the paragraph lookup below validates it as a UUID and throws
   * `UserInputError` deep inside the lazy-load, which fails the entire node.
   * An unresolvable target is a per-edit problem, not a turn failure, so
   * surface it as a typed `redline-edit-error` (matching the missing-<p> and
   * missing-paragraph paths) and let the rest of the stream proceed.
   */
  if (!documentIdSchema.safeParse(documentId).success) {
    const message = `RedlineAgent emitted an invalid documentId "${documentId}" for paragraph "${paragraphId}".`;
    logger.warn(message);
    return [createRedlineEditError({ documentId, locationAnchor: paragraphId, message })];
  }

  const html = stripSelectionMarkers(rawHtml);

  const hast = parseHtmlToHast(html);
  const paragraphEl = findFirstParagraphElement(hast);
  if (!paragraphEl) {
    const message = `RedlineAgent missing <p> element in html for paragraph "${paragraphId}".`;
    logger.error(message, { documentId, html });
    errorReporter.captureMessage(message, {
      extra: { documentId, html, paragraphId },
      level: 'error',
    });
    return [createRedlineEditError({ documentId, locationAnchor: paragraphId, message })];
  }

  const reviewRanges = hastToReviewRanges(paragraphEl);

  let paragraphInfo: ParagraphInfo | undefined;
  let resolvedParagraphId = paragraphId;
  try {
    paragraphInfo = await paragraphState.getParagraphInfoByLocationAnchorWithLazyLoad(
      documentId,
      paragraphId
    );

    // Fallback: the LLM sometimes puts the correct anchor in the HTML <p id>
    // attribute but a wrong value in suggest.id. Try the HTML id if they differ.
    if (!paragraphInfo && reviewRanges.id && reviewRanges.id !== paragraphId) {
      const fallback = await paragraphState.getParagraphInfoByLocationAnchorWithLazyLoad(
        documentId,
        reviewRanges.id
      );
      if (fallback) {
        const recoveryMessage = `RedlineAgent: suggest.id "${paragraphId}" not found; recovered using HTML <p id> "${reviewRanges.id}" for document "${documentId}".`;
        logger.warn(recoveryMessage);
        errorReporter.captureMessage(recoveryMessage, {
          extra: { documentId, htmlParagraphId: reviewRanges.id, paragraphId },
          level: 'info',
        });
        paragraphInfo = fallback;
        resolvedParagraphId = reviewRanges.id;
      }
    }
  } catch (error) {
    /**
     * The lazy document load can throw for a valid-looking-but-unresolvable
     * documentId (e.g. a deleted doc → "Document with id X not found"). That's
     * a per-edit failure, not a node failure, so convert it to a
     * `redline-edit-error` like the other unresolvable-target paths. But a user
     * Stop must always propagate — never be swallowed into an edit error — so
     * re-throw any cancellation/abort/timeout first, whether it arrives as a
     * DOMException abort or a Temporal CancelledFailure/TimeoutFailure.
     */
    if (getIsCancellationError(error)) throw error;
    const message = `RedlineAgent could not load document "${documentId}" for paragraph "${paragraphId}": ${error instanceof Error ? error.message : String(error)}`;
    logger.warn(message);
    errorReporter.captureMessage(message, {
      extra: { documentId, paragraphId },
      level: 'warning',
    });
    return [createRedlineEditError({ documentId, locationAnchor: paragraphId, message })];
  }

  // Return an error in the jsonl output if the paragraph is not found.
  if (!paragraphInfo) {
    const message = `RedlineAgent could not find paragraph "${paragraphId}" in document "${documentId}".`;
    logger.error(message);
    const allAnchors = paragraphState.getAllLocationAnchorsForDocument(documentId);
    errorReporter.captureMessage(message, {
      extra: {
        availableAnchorsCount: allAnchors.length,
        availableAnchorsSample: allAnchors.slice(0, 50),
        documentId,
        htmlParagraphId: reviewRanges.id,
        paragraphId,
      },
      level: 'error',
    });
    return [createRedlineEditError({ documentId, locationAnchor: paragraphId, message })];
  }

  const existingDiff = extractedSegmentsToDiffSegments(paragraphInfo.segments);

  const actions = reviewRanges.ranges.map((range, index) => {
    const content = extractSingleRangeContent(reviewRanges.text, index);
    return {
      action: 'modify',
      content,
      groupId: generateSuggestedEditGroupId(),
      reasoning: range.reasoning,
      visualDiffSegments: createVisualDiffInRange(existingDiff, content),
    } satisfies CodeBlockRedlineEdit['actions'][number];
  });

  /**
   * The prompt forbids formatting-only edits, but when the model emits one
   * anyway the proposed text is byte-identical to the original: the diff has
   * no insert/delete segments, applying would be a silent no-op, and the user
   * would see a normal-looking card that highlights nothing and navigates
   * nowhere. Surface it as an error card instead of applying.
   */
  const hasTextChange = actions.some(action =>
    action.visualDiffSegments.some(segment => Boolean(segment.newInsert || segment.newDelete))
  );
  if (!hasTextChange) {
    const message = `RedlineAgent produced an edit for paragraph "${resolvedParagraphId}" in document "${documentId}" that changes no text — formatting-only edits are not supported.`;
    logger.error(message, { documentId, html });
    errorReporter.captureMessage(message, {
      extra: { documentId, html, paragraphId: resolvedParagraphId },
      level: 'error',
    });
    return [createRedlineEditError({ documentId, locationAnchor: resolvedParagraphId, message })];
  }

  const edit: CodeBlockRedlineEdit = {
    actions,
    category: 'in-paragraph',
    documentId,
    locationAnchor: resolvedParagraphId,
    segments: paragraphInfo.segments,
    textHash: hash(paragraphInfo.text),
    type: 'redline-edit',
  };

  /**
   * Preview mode returns the card without applying. PDF (Apryse) documents
   * are read-only — their coordinate-based paragraph anchors don't exist in
   * the collab editor, so applying would throw — and signing-locked documents
   * (sent for signature or signed) must not receive new edits: both always
   * take this path.
   */
  if (
    mode === 'preview' ||
    getIsPdfLocationAnchor(edit.locationAnchor) ||
    (await editorPool.getSigningLockState(edit.documentId))
  ) {
    return [edit];
  }

  try {
    const editor = await editorPool.getOrCreateEditor(edit.documentId);
    AlineTransforms.withoutNormalizing(editor, () => {
      applyInParagraphEditOutput(editor, {
        authorId: userId,
        editOutput: { edit },
        groupIds: edit.actions.map(a => a.groupId),
        makeSource,
        reviewText: reviewRanges.text,
      });
    });
  } catch (error) {
    const message =
      error instanceof Error
        ? `RedlineAgent applyInParagraphEditOutput threw for paragraph "${resolvedParagraphId}": ${error.message}`
        : `RedlineAgent applyInParagraphEditOutput threw for paragraph "${resolvedParagraphId}".`;
    logger.error(message, error);
    errorReporter.captureException(error, {
      extra: { documentId, originalParagraphId: paragraphId, paragraphId: resolvedParagraphId },
    });
    return [createRedlineEditError({ documentId, locationAnchor: resolvedParagraphId, message })];
  }

  return [edit];
}
